// apps/main/src/clark-widget/index.jsx — RUN WW. The homepage Clark widget.
//
// Mounts into the existing homepage exhibit (ops/homescreen-arc/master_final.html's `.ck-card`),
// replacing its static markup with a live, working Clark: real question in, real answer out, real
// authorities in the rail, signed out. Follows the existing marketing-island pattern (src/auth,
// src/checkout, src/header) — esbuild entry, no ClerkProvider tree of its own; it talks to
// window.Clerk directly (the CDN clerk-js singleton the header island already loads on this page)
// for the one thing it needs from auth: a token to attach when a visitor happens to be signed in.
//
// 🔴 REUSE, NOT REBUILD. The two hard problems here — SSE parsing/timeout bounds, and rendering a
// gate-verified v2 answer with its authority rail — already exist and are already right:
// streamResearchAnswer (answerApi.js) and ClarkAnswerV2 (answer/ClarkAnswerV2.jsx) are the same
// modules the signed-in app uses. This file is the anonymous SHELL around them: classify, route,
// cap, gate. It does not re-implement either.
//
// 🔴 THE SIGN-UP MODAL IS NOT A NEW IMPORT OF SignupGate.jsx — AND IT DOESN'T NEED TO BE.
// SignupGate.jsx imports src/auth/index.jsx for openAuthModal — and src/auth/index.jsx is ALSO its
// own esbuild entry point (auth.bundled.js), already loaded on this page, with import-time code
// that mounts itself into #cc-auth-react-root. Importing that module from a SECOND bundle would
// bundle and RUN that mount code again, in a second bundle, against the same DOM node — two React
// roots racing over one element. So this widget calls window.Clerk.openSignUp/openSignIn instead —
// and auth/index.jsx (already loaded) MONKEY-PATCHES those two methods to open the real custom
// navy modal rather than Clerk's stock one (see its "App-controlled opener" block). Verified live:
// window.Clerk.openSignUp() from this widget opens the actual .cc-auth-* modal, not a plain Clerk
// dialog — no cross-bundle coordination needed, and no visual downgrade either.

import { createRoot } from "react-dom/client";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  classifyClarkTurn,
  streamResearchAnswer,
} from "../../client/src/screens/Clark/answerApi.js";
import ClarkAnswerV2 from "../../client/src/screens/Clark/answer/ClarkAnswerV2.jsx";
// RUN CH3 — the handoff channel, reused rather than reinvented. See handOffToClark below.
import { openInClarkFromHome } from "../../client/src/screens/Clark/handoff.js";
// RUN CK2 AMENDMENT §2.2 — the detector lives beside the ONE jurisdiction vocabulary it maps
// against, so a detected id and a picked id are the same value by construction.
import { jurisdictionFromQuestion } from "@citeclerk/goodlaw/jurisdictionFromQuestion.js";
// 🔴 RUN FZ — the frozen chip answers. GENERATED from ops/run-fz/capture.json; see frozen-chips.js.
import { frozenAnswerFor } from "./frozen-chips.js";
import JurisdictionPicker, { ENTRY } from "@citeclerk/ui/JurisdictionPicker";
/* 🔴 RUN CQ2 — THE HOMEPAGE WIDGET WAS SENDING NO SYSTEM MESSAGE AT ALL. See sendConversational. */
import { CLARK_SYSTEM_PROMPT } from "../../client/src/screens/Clark/systemPrompt.js";
import "./widget.css";
// RUN AB — §2 identity and §7 the capacity refusal.
import CapacityModal from "./CapacityModal.jsx";
// RUN TU — the anonymous request's headers and its one local refusal, shared with the SPA path.
// getFingerprint() moved behind it; this island no longer calls the fingerprint module directly.
import { anonRequest } from "../../client/src/lib/anonFetch.js";
// RUN AN — the widget-side Plausible events. Guarded no-op when the page carries no tag.
// ⚠️ RUN MZ3 — AN's sibling getFingerprint import is deliberately NOT carried across:
// RUN TU moved that call behind anonRequest(), so this island no longer imports that module.
import { track } from "../analytics.js";

// ── auth: talk to the CDN clerk-js singleton directly, no ClerkProvider needed ──────────────────
function waitForClerk(timeoutMs) {
  return new Promise((resolve) => {
    if (window.Clerk) return resolve(window.Clerk);
    let waited = 0;
    const step = 50;
    const t = setInterval(() => {
      if (window.Clerk) { clearInterval(t); resolve(window.Clerk); }
      else if ((waited += step) >= timeoutMs) { clearInterval(t); resolve(null); }
    }, step);
  });
}

async function widgetFetch(url, opts = {}) {
  let token = null;
  try {
    if (window.Clerk?.session) token = await window.Clerk.session.getToken();
  } catch { /* anonymous — no token */ }
  /* 🔴 RUN TU — WHAT AN ANONYMOUS CALL CARRIES IS DECIDED IN ONE PLACE NOW, NOT TWO.
     RUN AB put the `x-cc-fp` fingerprint here because the widget was the only anonymous send path.
     RUN CH3 moved the turn: /clark makes every call after the first, through main.jsx's
     authedFetch, which never learned about it — so a stranger's first turn was identified by
     fingerprint and their second by cookie-or-IP. lib/anonFetch.js is the single decision both
     wrappers now ask, and Turnstile joins it there rather than being added twice.

     🔴 `pre.refusal` IS THE FAIL-CLOSED PATH. A site key exists and the browser could not reach
     Cloudflare, so there is no token to send and the request is not made — the visitor gets the
     degraded "we couldn't verify" body, applyRefusal() reads `verification: true`, hasAnswered
     stays false and the composer stays live. Their free question is untouched because nothing was
     ever asked of the server. */
  const pre = await anonRequest(url, { signedIn: Boolean(token) });
  if (pre.refusal) return pre.refusal;
  return fetch(url, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...pre.headers,
    },
  });
}

/**
 * 🔴 RUN CH3 — THE WIDGET IS A LAUNCHER. THE ANSWER IS RENDERED BY THE REAL APP.
 *
 * The stranger asks here and LANDS ON /clark with their answer already in the thread and its
 * authorities in the rail — the full product, shell and all, at the address the product has. The
 * widget's job ends at the handoff.
 *
 * 🔴 FETCHED BEFORE NAVIGATING, AND THAT ORDER IS THE FEATURE. /clark hydrates from the payload
 * instead of re-asking, so: no second synthesis of an answer already paid for, no anonymous turn
 * spent twice, and no blank app shell sitting on a model call while a first-time visitor decides
 * whether this product works. They watch the answer arrive HERE, then the page turns.
 *
 * `location.href`, not a router push: the homepage is a static page and /clark is the SPA. This is
 * a real navigation between two documents, which is also what makes the URL read /clark.
 *
 * Degrades honestly: if storage refused the payload (Safari private mode, quota) the visitor still
 * lands on /clark with their question adopted and a line saying the answer did not travel — the
 * behaviour handoff.js's header already specifies for the other legs.
 */
function handOffToClark(answer, { q, askedAt, jurisdiction = null }) {
  let href = "/clark";
  try {
    /* 🔴 RUN CK2 AMENDMENT §2.1 — SEAM B CLOSED. `openInClarkFromHome` has always taken a
       `jurisdiction` argument (handoff.js:199) and this call has never passed one, so a selection
       made in the widget's own picker died at the navigation: Clark opened at "All jurisdictions"
       no matter what the visitor had chosen. The scope now travels with the answer it scoped. */
    href = openInClarkFromHome({ ...answer, askedAt }, { q, jurisdiction });
  } catch {
    /* A handoff must never strand the visitor on the homepage: /clark still opens. */
  }
  window.location.href = href;
}

function openSignUp() { try { window.Clerk?.openSignUp?.({}); } catch { /* Clerk not ready */ } }
function openSignIn() { try { window.Clerk?.openSignIn?.({}); } catch { /* Clerk not ready */ } }

// ── the conversational door (off-scope / product questions) ────────────────────────────────────
async function fetchConversational(q) {
  let res;
  try {
    /* 🔴 RUN CQ2 §2 — THE PERSONA TRAVELS NOW, AND IT IS THE APP'S OWN, NOT A SECOND COPY.
       This body used to be `{ messages: [{ role: "user", content: q }] }` — no system message.
       `routes/clark.js:239-246` builds `systemParts` from `role: "system"` messages and nothing
       else, so on this door it was ALWAYS empty, and `buildClarkSystemBlocks` then sends
       CLARK_CITE_ADDENDUM alone (clarkSystem.js:44: `persona.length ? … : addendum`). Clark
       answered a stranger with the citation rules and no idea what CiteClerk is — which is why
       "What plans does CiteClerk offer?" came back in the THIRD PERSON, telling a visitor standing
       on citeclerk.com to go check the CiteClerk website.
       ⭐ NOTHING IS WRITTEN HERE. This is the identical string `screens/Clark/index.jsx` has always
       sent from inside the app, whose "## Plans and pricing" section is IMPORTED from
       @citeclerk/plan (systemPrompt.js:80 → PLANS_AND_PRICING_MD) precisely so no price, tier or
       allowance is ever typed into a prompt — RUN H's fix, reused rather than re-litigated. Both
       the plan-copy gate and the claims gate already scan that file (plan-copy-gate.mjs:572,
       claims-gate.mjs:153); sending it does not add a surface either gate was not already reading.
       ⚠️ The 100 kb body limit on this route (server.js:225) was raised for exactly this prompt. */
    res = await widgetFetch("/api/clark", {
      method: "POST",
      body: JSON.stringify({
        messages: [
          { role: "system", content: CLARK_SYSTEM_PROMPT },
          { role: "user", content: q },
        ],
      }),
    });
  } catch {
    return { status: "error", message: "I couldn't reach the server just now. Try again in a moment." };
  }
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    return { status: "capped", data };
  }
  /* RUN AB — `data` is threaded through unchanged so the caller can read `capacity` /
     `verification` and pick which of the THREE refusals to show. Before this, every 429 rendered
     one string ("That's your free question for now — sign in"), which is false for two of the
     three causes: a capacity refusal is OUR ceiling, not their turn, and a failed bot check has
     spent nothing at all. */
  if (!res.ok) {
    return { status: "error", message: data?.reply || "Something went wrong on my side. Try again." };
  }
  /* 🔴 RUN CQ2 §1 — `citationPayload` USED TO BE DROPPED HERE, AND IT IS THE ANSWER.
     This line read `return { status: "answer", reply: data.reply || "" }`. The server had already
     resolved the citation — RUN CP2 measured the Cite chip returning
     `Erie R.R. v. Tompkins, 304 U.S. 64 (1938).` with `confidence: "high"` — and this return threw
     it away, so the stranger read "Got it — high confidence. Want a pincite, parallel cite, or
     short form?" and was offered a pincite for a citation that was never on screen. Zero of the
     four conversational chips rendered an authority, on a product whose headline is
     "answers — and authorities".
     It rides on as `citationPayload` under its own name because that is what `/clark` already
     renders it as (`thread/Turn.jsx:176` → `CitationBlock`). No new shape, no adapter. */
  return { status: "answer", reply: data.reply || "", citationPayload: data.citationPayload || null };
}

const CHIPS = [
  /* 🔴 RUN RQ · §3 — Adam replaced this question. It was *"What is the standard for summary
     judgment in federal court?"*, whose frozen answer returned FOUR authorities, ALL CASES, plus
     `Fed. R. Civ. P. 56` sitting unresolved in See-also. The brief: find a question that returns
     BOTH statutes and cases, resolved, and whose answer is excellent.

     ⭐ SUPPLEMENTAL JURISDICTION IS STRUCTURALLY TWO-TYPED, WHICH IS WHY IT WON. The doctrine IS a
     statute (28 U.S.C. § 1367) that codified a case (Gibbs) and is bounded by a second statute
     (§ 1332), so both types are load-bearing in the answer rather than incidental to it. MEASURED:
     six cold drives, and § 1367 arrived resolved at rank 1 on every one.

     ⚠️ FOUR OTHER CANDIDATES WERE DRIVEN COLD AND LOST ON MEASUREMENT, NOT ON TASTE.
       · qualified immunity (§ 1983)  — returned 1 statute + 6 cases on one roll and ZERO statutes
         on the next. Unstable on the exact property the chip is being chosen for.
       · diversity jurisdiction (§ 1332) — TRUNCATED mid-sentence on BOTH rolls ("adopted the ",
         "The Supreme Court clarified the"). Reproducible, and not a thing to freeze.
       · ADA reasonable accommodation — a literal markdown fence leaked into the prose.
       · Title VII retaliation — the gate removed a sentence and left the next paragraph opening
         *"This heightened causation standard…"* with no antecedent.
       · FLSA overtime — broken sentence joins ("…narrowly construed.the employee's actual job
         duties…") and a stripped C.F.R. row.
     Every one of those is a defect in an ANSWER, and the lever is the QUESTION: not one word of
     any answer was written, composed, edited, tidied or improved. See RUN RQ's report. */
  { label: "Research", q: "When can a federal court exercise supplemental jurisdiction over state law claims?" },
  { label: "Cite", q: "What's the Bluebook citation for Erie Railroad v. Tompkins?" },
  { label: "Check", q: "Is this citation correct: 28 USC 1332?" },
  /* 🔴 RUN FZ — Adam replaced this question. The pincite wording ASKED FOR A RULE NUMBER the
     product cannot reliably produce: RUN CQ2 §4 measured `resolveRule` tying the real pincite
     entry (B10.1.2 / Rule 10.3.2) against a STOPWORD match on "the" and losing on object key
     order, and RUN CV caught a sibling phrasing answering with a rule about CONSTITUTIONS. A
     chip that advertises a gap is worse than one that does not. `id.` vs `supra` was the
     strongest Rule answer across CV and CF, and it promises nothing the product cannot keep. */
  { label: "Rule", q: "When do I use id. versus supra?" },
  { label: "Summarize", q: "Summarize the opinion in Celotex Corp. v. Catrett." },
  { label: "About", q: "What plans does CiteClerk offer?" },
];

function ClarkWidget() {
  // phase: idle | thinking | streaming | answer | conversational | declined | error | capped
  const [phase, setPhase] = useState("idle");
  const [value, setValue] = useState("");
  const [askedQ, setAskedQ] = useState(null);
  const [live, setLive] = useState(null);
  const [conversationalReply, setConversationalReply] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(false); // one free turn, client-side courtesy gate
  const [jurisdiction, setJurisdiction] = useState({ sel: [], preset: null });
  const [pickerOpen, setPickerOpen] = useState(false);
  const answerRef = useRef(null); // §4.3 — auto-open the authorities rail, see the effect below
  const inFlight = useRef(false); // double-submit guard
  // ── RUN AB §7 ──────────────────────────────────────────────────────────────────────────────
  // `refusal` holds the SERVER's refusal body, so the copy a visitor reads is the copy the module
  // that refused them chose. Three causes, three renderings, and the discriminators are the flags
  // the server sets — never the HTTP status, which is 429 for all three.
  const [refusal, setRefusal] = useState(null);
  const composerRef = useRef(null); // focus returns HERE when the modal closes, per §7

  const scopeParam = jurisdiction.sel?.length ? jurisdiction.sel.map((s) => s.id).join(",") : "all";

  // §4.3 — auto-open the authorities rail for a one-turn stranger. "Show Authorities" is
  // ClarkAnswerV2's own internal useState (default false, no prop to set it) — rather than fork or
  // extend the shared component for this, click its own toggle once, the moment it mounts with
  // authorities to show. Idempotent per answer (an already-open rail is a harmless second click's
  // worth of no-op if this ever double-fires).
  useEffect(() => {
    if (phase !== "answer" || !live?.authorities?.length) return;
    const t = setTimeout(() => {
      const btn = answerRef.current?.querySelector('[data-testid="clark-action-authorities"]');
      if (btn && btn.getAttribute("aria-expanded") !== "true") btn.click();
    }, 50);
    return () => clearTimeout(t);
  }, [phase, live]);

  /**
   * 🔴 RUN AB — WHICH OF THE THREE REFUSALS IS THIS? THE STATUS DOES NOT SAY.
   *
   * All three arrive as HTTP 429 with `capped: true`, which is why the widget used to render one
   * message for all of them. The server distinguishes them with two additive flags:
   *
   *   `capacity: true`     → OUR global ceiling filled. The capacity modal. 🔴 The visitor's own
   *                          turn was NOT spent (checkClarkCap reserves and refuses BEFORE
   *                          trial.spend() is ever called), so `hasAnswered` stays FALSE and the
   *                          composer stays live — they may try again in a minute and it will work.
   *   `verification: true` → the bot check failed. Retry. Also NOT their turn: Turnstile is gate 1
   *                          and the trial is gate 3, so `hasAnswered` stays false here too.
   *   neither              → their own grant is spent. Sign in. This one DID cost them the turn.
   *
   * The `hasAnswered` distinction is the whole point of §7's "the visitor's own turn must not be
   * spent on a capacity refusal": setting it true would lock their composer and open the sign-up
   * modal on the next keystroke, which is the UI telling them they used a turn they still have.
   */
  const applyRefusal = useCallback((result) => {
    const body = result?.data || result?.block || {};
    if (body.capacity) {
      const variant = body.variant === "daily" ? "daily" : "hourly";
      /* 🔴 RUN AN — THE EVENT THAT EARNS ITS KEEP. RUN AB's ceiling is $5/hour and $20/day, which
         at the measured cost is roughly 500 turns an hour. If this fires repeatedly on launch day
         the ceiling is too low, and that has to be visible the same day rather than arriving as a
         support email a week later. `variant` is carried so hourly and daily are separable: they
         mean different things (a spike vs. a budget genuinely exhausted) and want different fixes.
         Fired HERE, in the branch that recognises the refusal — one refusal, one event — rather
         than from CapacityModal's render or a mount effect, either of which fires again on any
         re-render and would inflate exactly the number Adam is reading. */
      track("capacity_modal_shown", { variant });
      setRefusal({ kind: "capacity", variant,
                   title: body.title, reply: body.reply });
      setPhase("idle");
      return; // hasAnswered deliberately NOT set — their turn is intact.
    }
    if (body.verification) {
      setRefusal({ kind: "verification", title: body.title, reply: body.reply });
      setPhase("error");
      setErrorMessage(body.reply || "That security check didn't go through. Please try your question again.");
      return; // hasAnswered deliberately NOT set — their turn is intact.
    }
    setRefusal({ kind: "trial", reply: body.reply });
    setPhase("capped");
    setHasAnswered(true); // this refusal, and only this one, means the turn is gone.
  }, []);

  const runTurn = useCallback(async (q) => {
    const text = String(q || "").trim();
    if (!text) return; // empty / whitespace-only: no-op, no call
    if (inFlight.current) return; // double-submit guard: Enter twice, rapid chip clicks
    if (hasAnswered) { openSignUp(); return; } // follow-up attempt → the modal, never a second call
    inFlight.current = true;
    /* 🔴 RUN CH3 — STAMPED WHEN THEY ASKED, NOT WHEN /clark LOADS. The turn that appears in the
       thread claims a time; the honest one is the moment the visitor pressed send here, not the
       moment the next page finished booting. PART 4.4's rule, applied to this leg. */
    const askedAt = Date.now();

    /* 🔴🔴 RUN CK2 AMENDMENT §2.2 / §3 — A STATED JURISDICTION SETS THE PICKER. IT NEVER SCOPES
       INVISIBLY. Adam's failing question ("Under NJ rules, …") shipped `scope: "all"` because the
       picker defaults to `{ sel: [], preset: null }` and nothing read the forum out of the
       question — so nothing was filtered on any lane and a Third Circuit case came back for a New
       Jersey question. The detector reads only a STATED forum ("In NJ,", "Under New Jersey law,")
       and refuses a jurisdiction word inside a party name; see its own header and test file.

       🔴 THE PICKER IS UPDATED SO THE USER CAN SEE IT. A narrowed search nobody can inspect is
       worse than no narrowing — it drops controlling authority for a reason the reader cannot find.
       `setJurisdiction` repaints the pill ("1 selected") and the chosen row is checked inside the
       sheet, so the scope is visible and changeable before or after asking.

       ⚠️ ONLY WHEN THE PICKER IS UNTOUCHED. If the visitor has already chosen a jurisdiction, this
       does NOTHING — including when the question states a different one. AMENDMENT §3.1 asks which
       should win and says to report the options rather than choose, so the conflict case is left
       exactly as it is today (the picker's value ships) and is reported. This is not a ruling.

       ⚠️ `effectiveScope` is computed here rather than read from `scopeParam` because setState is
       async: the request below would otherwise ship the PREVIOUS render's scope and the picker
       would show a jurisdiction that had not been applied — the invisible-scope defect inverted. */
    const alreadyPicked = !!jurisdiction.sel?.length;
    const detected = alreadyPicked ? null : jurisdictionFromQuestion(text);
    const detectedEntry = detected ? ENTRY[detected] : null;
    if (detectedEntry) setJurisdiction({ sel: [detectedEntry], preset: null });
    const effectiveScope = detectedEntry ? detectedEntry.id : scopeParam;
    setAskedQ(text);
    setValue("");
    setErrorMessage(null);
    setConversationalReply(null);
    setLive(null);
    setPhase("thinking");

    /* ══ 🔴 RUN FZ — THE FROZEN CHIPS, AND WHY THE INTERCEPTION IS *HERE* AND NOT ANYWHERE LATER.
     *
     * A fixed input deserves a fixed output: the six TRY chips send six fixed strings, and every
     * word below was driven through the live pipeline once and read before it was frozen.
     *
     * 🔴 THIS SITS BEFORE `widgetFetch` ISSUES ANYTHING, AND THAT POSITION IS THE WHOLE DESIGN.
     * The anonymous trial is spent SERVER-SIDE — `checkClarkCap` → `trial.spend()` inside
     * routes/clark.js and routes/clark-answer.js. A turn answered here makes NO HTTP REQUEST AT
     * ALL, so no cap gate runs, no `anon_trial` row is claimed, no model is called and nothing is
     * metered. Adam's ruling — a frozen answer costs nothing and must not spend the visitor's one
     * free question — is satisfied by construction rather than by a flag someone has to remember.
     *
     * 🔴 EXACT TEXT ONLY. `frozenAnswerFor` is a plain `Map.get` on the raw string: chip text hits,
     * one character different misses and falls through to the live turn below. `text` is already
     * `String(q||"").trim()` and the keys come from the same CHIPS[].q constant the buttons use, so
     * the two cannot drift apart without the lookup simply missing — which is the safe direction.
     *
     * ⚠️ `hasAnswered` IS DELIBERATELY NOT SET. Every other branch of this function sets it, which
     * locks the composer and opens the sign-up modal on the next keystroke. That is correct for a
     * turn that COST something. A frozen answer costs nothing, so a visitor who clicks a chip still
     * has their real question left — which is the point of putting a chip in front of them.
     *
     * The payload handed to `handOffToClark` is the SAME SHAPE a live turn hands it, so /clark
     * adopts it through the identical effect and renders it with the identical components: the v2
     * envelope and its authority rail for the two research chips, `reply` + `citationPayload` (and
     * therefore `CitationBlock`) for the other four. There is no second renderer to keep in step. */
    const frozen = frozenAnswerFor(text);
    if (frozen) {
      inFlight.current = false;
      handOffToClark(
        frozen.kind === "research"
          ? { q: text, scope: frozen.scope, answerId: frozen.answerId, prose: frozen.prose,
              segments: frozen.segments, authorities: frozen.authorities, stripped: frozen.stripped,
              unchecked: frozen.unchecked, quotes: frozen.quotes, badge: frozen.badge,
              jurisdiction: frozen.jurisdiction }
          : { q: text, reply: frozen.reply, citationPayload: frozen.citationPayload },
        { q: text, askedAt },
      );
      return;
    }

    /* ══ 🔴🔴 RUN CW §2(c) — A SIGNED-IN VISITOR NAVIGATES IMMEDIATELY AND CLARK DOES THE ASKING.
     *
     * The island showed thinking dots in the blue panel and then navigated, so a signed-in user
     * watched a spinner HERE and then watched Clark's own indicator THERE. RUN MZ3 made this a
     * launcher; this finishes that. Clark shows its own indicator while the answer streams.
     *
     * 🔴 THE AUTH CHECK IS HERE, BEFORE ANY FETCH DECISION, WHICH IS ADAM'S REQUIREMENT: a signed-in
     * visitor must not pay a round trip they do not need. `window.Clerk?.session` is a SYNCHRONOUS
     * read of the clerk-js singleton the header island already loaded on this page — no await, no
     * request. `widgetFetch` awaits `session.getToken()`, but that is the TOKEN, not the question of
     * whether one exists.
     *
     * ⚠️ ANONYMOUS VISITORS KEEP FETCH-THEN-NAVIGATE, AND THAT IS NOT CONSERVATISM. Driven against
     * production: an anonymous typed question in Clark renders "Quick check before your query" and
     * never issues an answer call at all — the anonymous door is Turnstile-gated
     * (client/src/lib/turnstileClient.js). A token CANNOT travel in the handoff to cover it: that
     * module's own header says a Turnstile token is SINGLE-USE and ~300s, so it "does not carry the
     * token, and must not be made to". Navigating a stranger first would put a bot challenge between
     * them and the answer they came for. Signed-in visitors never reach that module at all — the
     * gate is anonymous-only — so for them there is nothing to lose and a round trip to save.
     *
     * ⚠️ FROZEN CHIPS ARE EXCLUDED REGARDLESS OF AUTH, AND THE CHECK BELOW SITS AFTER THEIRS ON
     * PURPOSE. RUN FZ's rule stands: a frozen answer makes NO HTTP request, spends no turn and costs
     * nothing. Navigating first would make Clark ask for real and spend a signed-in user's metered
     * turn on an answer this island already has, word for word. The dots never show for a chip
     * anyway — there is no network between the click and the handoff. */
    const signedIn = Boolean(window.Clerk?.session);
    if (signedIn) {
      inFlight.current = false;
      /* No `prose`: the payload carries the QUESTION and an intent. Clark's adopt effect sends it,
         which is the only synthesis that happens on this path — not a second one. */
      handOffToClark(
        { q: text, intent: "ask" },
        { q: text, askedAt, jurisdiction: effectiveScope === "all" ? null : effectiveScope },
      );
      return;
    }


    try {
      const decision = classifyClarkTurn(text);
      if (decision.route === "research") {
        const result = await streamResearchAnswer(
          widgetFetch,
          { q: text, scope: effectiveScope },
          (patch) => { setPhase("streaming"); setLive(patch); }
        );
        if (result.status === "capped") { applyRefusal(result); return; }
        if (result.status === "disabled" || result.status === "error") {
          setPhase("error"); setErrorMessage(result.message); setHasAnswered(true); return;
        }
        // 'answer' or 'decline' — both are real, complete, gate-produced outcomes.
        /* RUN AN — a stranger received an answer. Fired on the RESULT, imperatively, not from an
           effect on `phase`: an effect re-runs on every dependency change (and twice under
           StrictMode, which the SPA does enable even though this island does not), and this number
           is the denominator of the whole funnel. `outcome` keeps 'decline' separable from
           'answer' — a decline is a complete, gate-produced turn and the visitor did receive a
           reply, so it counts here, but it is not the same product event and should not be read as
           one. */
        track("widget_answered", { route: "research", outcome: result.status });
        // ⚠️ RUN MZ3 — AN's setLive/setPhase("answer") are NOT carried across. RUN CH3/CQ2/FZ turned this island
        // ⚠️ RUN MZ3 — into a launcher: it hands the answer to /clark and never renders it inline,
        // ⚠️ RUN MZ3 — so setting local answer state here would paint a second, competing surface.
        setHasAnswered(true);
        handOffToClark(result.data, { q: text, askedAt, jurisdiction: effectiveScope === "all" ? null : effectiveScope });
      } else {
        const result = await fetchConversational(text);
        if (result.status === "capped") { applyRefusal(result); return; }
        if (result.status === "error") {
          setPhase("error"); setErrorMessage(result.message); setHasAnswered(true); return;
        }
        // RUN AN — the off-scope / product-question door is still "a stranger receives an
        // answer"; `route` separates it from a real research turn.
        track("widget_answered", { route: "conversational", outcome: "answer" });
        // ⚠️ RUN MZ3 — as above, AN's setConversationalReply/setPhase are dropped: this branch hands off too.
        setHasAnswered(true);
        /* RUN CQ2 §1 — the citation travels with the reply. `handoff.js`'s writer carries it
           through localStorage and the adopt effect on /clark renders it; see both ends. */
        handOffToClark({ q: text, reply: result.reply, citationPayload: result.citationPayload },
                       { q: text, askedAt, jurisdiction: effectiveScope === "all" ? null : effectiveScope });
      }
    } finally {
      inFlight.current = false;
    }
  }, [hasAnswered, scopeParam, jurisdiction]);

  const onSubmit = (e) => { e?.preventDefault?.(); runTurn(value); };
  /* RUN AN — chip_clicked. `label` (not the question text) is the prop: it is the short stable
     token a funnel groups by, and the six questions are long enough that Plausible would truncate
     them. Fired FIRST, before the hasAnswered branch, so this counts CHIP CLICKS rather than chips
     that happened to run a turn — the two are the same number today only because CHIPS render in
     phase "idle" alone. */
  const onChip = (q, label) => {
    track("chip_clicked", { label });
    if (hasAnswered) { openSignUp(); return; }
    runTurn(q);
  };

  // Signed-out interception for every outbound/account-bound affordance the answer renders:
  // Open in Research, and "ask about an authority" (which composes a follow-up turn). Copy and
  // Show/Hide Authorities stay untouched — neither leaves the widget nor needs an account.
  const onOpenResearch = useCallback((e) => {
    if (!window.Clerk?.user) { e.preventDefault(); openSignUp(); }
    // Signed in: let the real <a href> navigation proceed untouched — it already carries
    // answerId/from=clark, so Research opens the actual answer, per handoff.js.
  }, []);
  const onAskAbout = useCallback(() => {
    // onAskAbout composes a NEW Clark turn (a follow-up) in the full app; here, that is exactly the
    // one-turn cap's business — route it through the same gate as any other follow-up attempt.
    openSignUp();
  }, []);

  return (
    <div className="ckw-root">
      <div className="ck-juris">
        <button type="button" className="jp-pill" onClick={() => setPickerOpen((o) => !o)}
                aria-haspopup="dialog" aria-expanded={pickerOpen} data-testid="clark-widget-jurisdiction">
          {jurisdiction.sel?.length ? `${jurisdiction.sel.length} selected` : "All jurisdictions"}
          <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 6 8 10 12 6" /></svg>
        </button>
        <JurisdictionPicker open={pickerOpen} onClose={() => setPickerOpen(false)} value={jurisdiction} onChange={setJurisdiction} />
      </div>

      <form className={"ck-bar" + (phase === "thinking" || phase === "streaming" ? " live" : "")} onSubmit={onSubmit}>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={hasAnswered ? "Sign in to keep going" : "Ask a legal question…"}
          aria-label="Ask Clark a legal question"
          data-testid="clark-widget-input"
          ref={composerRef}
          disabled={phase === "thinking" || phase === "streaming"}
          onFocus={() => { if (hasAnswered) openSignUp(); }}
        />
        {/* 🔴 RUN TD — INTENTIONAL TWIN OF THE CLARK APP'S SEND BUTTON. MOVE THEM TOGETHER.
            The reference is packages/ui/react/SearchComposer.jsx:140-150 (.cc-composer__go, styled
            in SearchComposer.css:80-113), which is what screens/Clark/Composer.jsx renders. This
            island cannot IMPORT it: SearchComposer is the whole composer, not a button, and pulling
            it in would replace this ck-bar form and move RUN DX's measured widths (640/598/280).
            So the glyph, sizing, stroke and all four states are copied VERBATIM instead. Was a
            15x15 arrow pointing UP (M8 13V3 / M4 7l4-4 4 4) against Clark's 19x19 arrow pointing
            RIGHT — two implementations that drifted because they were never one.
            ⚠️ If the reference changes, change this too; the CSS twin is in
            ops/homescreen-arc/master_final.html (.ck-send), which generates home.html. */}
        <button type="submit" className="ck-send" aria-label="Send message" data-testid="clark-widget-send"
                disabled={phase === "thinking" || phase === "streaming"}>
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12h14M13 6l6 6-6 6" /></svg>
        </button>
      </form>

      {phase === "idle" && (
        <div className="ck-try">
          <span className="ck-try-label">Try:</span>
          {CHIPS.map((c) => (
            <button key={c.label} type="button" className="ck-chip" data-testid="clark-widget-chip" onClick={() => onChip(c.q, c.label)}>{c.label}</button>
          ))}
        </div>
      )}

      {(phase === "thinking" || phase === "streaming") && (
        <div className="ckw-turn" data-testid="clark-widget-thinking">
          {/* 🔴 RUN CK2 §3 — THE QUESTION IS NOT ECHOED HERE. ADAM'S RULING: no text in the blue
              section, ever. RUN MZ3 made this island a launcher that hands the answer to /clark and
              never renders it inline (see its notes on runTurn's two branches) — but it removed only
              the TERMINAL render. The in-flight one survived: setPhase("thinking") at the top of
              runTurn and setPhase("streaming") in streamResearchAnswer's patch callback both land
              here, and this line painted the question under the input on every ask. That is the
              path MZ3 never covered, not a regression against it. The handoff is untouched. */}
          <div className="ckw-dots" aria-live="polite" aria-label="Clark is thinking">
            <span /><span /><span />
          </div>
        </div>
      )}

      {phase === "answer" && live && (
        <div className="ckw-turn" ref={answerRef} data-testid="clark-widget-answer">
          <ClarkAnswerV2 answer={live} onOpenResearch={onOpenResearch} onAskAbout={onAskAbout} />
        </div>
      )}

      {phase === "conversational" && (
        <div className="ckw-turn" data-testid="clark-widget-conversational">
          <p className="ckw-reply">{conversationalReply}</p>
        </div>
      )}

      {phase === "error" && (
        <div className="ckw-turn ckw-turn--error" data-testid="clark-widget-error">
          <p className="ckw-reply">{errorMessage || "Something went wrong. Try again in a moment."}</p>
        </div>
      )}

      {phase === "capped" && (
        <div className="ckw-turn" data-testid="clark-widget-capped">
          {/* RUN AB — the server's own words. trialRefusal() names WHICH ceiling was hit and says
              something different and true for each; hard-coding one string here is how a visitor
              refused by the IP ceiling got told they had used their own free question. */}
          <p className="ckw-reply">{refusal?.reply || "That's your free question for now — sign in to keep going."}</p>
          <div className="ckw-capped-ctas">
            <button type="button" className="ckw-cta-primary" onClick={openSignUp} data-testid="clark-widget-signup">Sign up</button>
            <button type="button" className="ckw-cta-secondary" onClick={openSignIn} data-testid="clark-widget-signin">Sign in</button>
          </div>
        </div>
      )}

      {/* 🔴 RUN AB §7 — THE CAPACITY MODAL. Rendered inside the widget's own tree (and therefore
          inside clark-widget.bundled.js) rather than as a new island: home.html and
          ops/homescreen-arc/assemble.mjs are RUN BB2's files for this run, and home.html is
          GENERATED by assemble.mjs in any case, so a hand-edit there would be overwritten by the
          next assemble. Mounting from the widget needs neither file touched.

          🔴 DISMISSING DOES NOT NAVIGATE. onClose only clears local state; the composer, the
          jurisdiction selection and any answer already on screen are untouched, and focus returns
          to the composer via composerRef. */}
      <CapacityModal
        open={refusal?.kind === "capacity"}
        variant={refusal?.variant}
        title={refusal?.title}
        reply={refusal?.reply}
        returnFocusRef={composerRef}
        onClose={() => setRefusal(null)}
      />
    </div>
  );
}

const mount = document.getElementById("cc-clark-widget-root");
if (mount) {
  waitForClerk(8000); // best-effort — the widget works before Clerk loads; a signed-in token just
  // arrives a moment later on whichever turn follows. Rendered immediately either way, per §0: a
  // stranger must see a working composer with no perceptible boot delay.
  createRoot(mount).render(<ClarkWidget />);
}
